import { getFirestore, collection, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from '@rsmaids/shared/services/firebaseApp';
import type { ContactListItem } from '@rsmaids/shared/types/contacts';
import { CONTACT_ROLES, CONTACT_NAME_ROLES } from '@rsmaids/shared/constants/roles';
import { auth } from '../auth/firebase';

let unsubscribe: Unsubscribe | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped by every stop, so a start that was still checking the role when the person signed out does nothing
let generation = 0;
let starting = false;

function mapDoc(id: string, d: Record<string, any>): ContactListItem {
  const firstName = d.firstName || '';
  const lastName = d.lastName || '';
  return {
    id,
    _id: id,
    firstName,
    lastName,
    displayName: d.name || `${firstName} ${lastName}`.trim() || (d.email ? String(d.email).split('@')[0] : ''),
    primaryEmail: d.email || '',
    primaryPhone: d.phone || '',
    company: d.company,
    avatar: d.avatar || '',
    labels: Array.isArray(d.labels) ? d.labels : [],
    labelIds: Array.isArray(d.labels) ? d.labels : [],
    emailSubscribed: d.emailSubscribed ?? false,
    smsSubscribed: d.smsSubscribed ?? true,
    zohoContactId: d.zohoContactId || undefined,
    customerCode: d.customerCode || undefined,
  };
}

// 'full': the whole list (owners and managers). 'names': only name, phone number and photo (a lead). 'none': nothing is requested.
type ContactsAccess = 'full' | 'names' | 'none';

/** What the signed-in account's role (the `role` claim on its sign-in token) may keep of the contact list. */
async function contactsAccess(): Promise<ContactsAccess> {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return 'none';
  try {
    let role = (await user.getIdTokenResult()).claims.role;
    // No role on the token at all: it may only just have been added, so ask for a fresh token once
    if (!role) role = (await user.getIdTokenResult(true)).claims.role;
    if (CONTACT_ROLES.includes(String(role) as (typeof CONTACT_ROLES)[number])) return 'full';
    if (CONTACT_NAME_ROLES.includes(String(role) as (typeof CONTACT_NAME_ROLES)[number])) return 'names';
    return 'none';
  } catch (error) {
    // Could not read the token (for example offline): do not decide here; the rules still apply and the listener handles a refusal.
    // The smaller copy is the safe one to keep until the role is known.
    console.error('[contactsSync] Could not read the role from the sign-in token:', error);
    return 'names';
  }
}

/** The part of a contact a lead keeps: who a phone number belongs to, nothing else. */
function toNameOnly(full: ContactListItem): ContactListItem {
  return {
    id: full.id,
    _id: full._id,
    firstName: full.firstName,
    lastName: full.lastName,
    displayName: full.displayName,
    primaryEmail: '',
    primaryPhone: full.primaryPhone,
    avatar: full.avatar,
    labels: [],
    labelIds: [],
    emailSubscribed: false,
    smsSubscribed: false,
  };
}

/** Removes every saved copy of customer contact data from this device (the list, each opened contact page and its billing). */
export function clearContactsCache(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key === 'cachedContacts' || key === 'contactsLastLoadTime' || key.startsWith('contact-'))) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
    // Firestore's own saved copy can only be cleared before the database is used, so flag it for the next app start (auth/firebase.ts)
    localStorage.setItem('rs.clearContactsCopy', '1');
  } catch { /* storage blocked: nothing was saved */ }
}

export function startContactsSync(onUpdate?: (contacts: ContactListItem[]) => void): void {
  if (unsubscribe || starting) return;
  starting = true;
  const startedIn = generation;

  contactsAccess().then((access) => {
    starting = false;
    // Owners and managers keep the whole list, a lead only names and numbers: everyone else does not even ask for it
    if (access === 'none' || startedIn !== generation || unsubscribe) return;
    listen(access, onUpdate);
  });
}

function listen(access: Exclude<ContactsAccess, 'none'>, onUpdate?: (contacts: ContactListItem[]) => void): void {
  const db = getFirestore(getFirebaseApp(), 'contacts');

  const subscribe = () => {
    unsubscribe = onSnapshot(
      collection(db, 'contacts'),
      (snap) => {
        const contacts = snap.docs.map(doc => {
          const full = mapDoc(doc.id, doc.data());
          return access === 'full' ? full : toNameOnly(full);
        });
        localStorage.setItem('cachedContacts', JSON.stringify(contacts));
        localStorage.setItem('contactsLastLoadTime', Date.now().toString());
        console.log(`[contactsSync] ${contacts.length} contacts cached`);
        onUpdate?.(contacts);
      },
      (err) => {
        unsubscribe = null;
        // The rules refused this account: stop for good (retrying cannot help) and drop what was saved
        if (err?.code === 'permission-denied') {
          console.warn('[contactsSync] this account may not read contacts, sync stopped');
          clearContactsCache();
          return;
        }
        console.warn('[contactsSync] listener error, resubscribing in 5s:', err);
        retryTimer = setTimeout(subscribe, 5000);
      }
    );
  };

  subscribe();
}

export function stopContactsSync(): void {
  generation++;
  starting = false;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
